/**
 * Types we (the Architecture Team) need to manage this monorepo and keep the
 * show running.
 *
 * TODO: More notes about build and runtime specs! Or maybe they belong in the
 * docs...
 */

import type {
  ComponentType,
  FunctionComponent,
  LazyExoticComponent,
} from "react";
import type { PackageJson } from "type-fest";
import { CORE_NAV_GROUPINGS_AND_ORDER } from "../constants.ts";
import z from "zod";

/**
 * ═════════════════════════════════════════════════════════════════════════
 *                          MISCELLANEOUS TYPINGS
 * ═════════════════════════════════════════════════════════════════════════
 */
/**
 * This is for the sentinel file that is created at a first-run of `pnpm dev`
 * OR when the developer explicitly runs `pnpm monorepo:run-initial-setup`. See
 * `scripts/src/constants:LOCAL_FABRIC_CONFIG_FILE` for the location of this
 * file.
 */
export type Fabric3Setup = {
  initialized: string;
  /**
   * The `INITIAL_SETUP_VERSION` that was current when this file was last
   * written. Absent on files written before versioning was introduced; in
   * that case the setup is treated as out-of-date and offered as an update.
   */
  version?: number;
  settings: {
    /**
     * True iff the developer accepted the recommended `.vscode/` config
     * during setup _and_ it was successfully applied. Used to decide
     * whether to offer to update on later setup runs.
     */
    usedRecommendedVSCodeConfig?: boolean;
  };
};

/**
 * ═════════════════════════════════════════════════════════════════════════
 *                          BUILD-TIME TYPINGS
 *
 * These are used only when building the Container.
 * ═════════════════════════════════════════════════════════════════════════
 */

/* ————————————————————————— Basic Stuff ————————————————————————— */

/**
 * How the application is grouped in the sidebar. This is used exclusively by
 * the Container (see `container:Container.tsx`) to tell `GKLayoutShell` how to
 * group apps.
 *
 * I am SCREAMING their names to indicate that they're variables that will
 * eventually be keys in an I18n map. I.e., these are *not* what we draw in the
 * sidebar! See the `i18n` folder in the Container.
 */
export type ApplicationGrouping =
  | (typeof CORE_NAV_GROUPINGS_AND_ORDER)[number]
  | null;
/**
 * The kinds of things in this monorepo (and at a fairly high level). Note that
 * we don't have this package (`definitions`) on this list.
 */
export type PackageType = "app" | "library" | "container" | "server";

/* ————————————————————————— package.json Typings ————————————————————————— */

type Fabric3Info = {
  type: PackageType;
  bootstrapped: string;
  team: string;
};

/**
 * Permissions schema that is used in package.json for defining app permissions.
 *
 * Right now we're only supporting `view` permissions. If we ever need or
 * want to support the other verbs, then update the model here pls.
 */
export const APP_PERMISSION_SCHEMA = z.object({
  view: z.array(z.string().min(1)).min(1),
});

export type AppPermissions = z.infer<typeof APP_PERMISSION_SCHEMA>;

/**
 * NOTE: You need to update `scripts/src/constants:APP_SIDEBAR_SCHEMA` if you
 * update this file (and vice-versa). Yeah it's double-work but (a) you won't
 * be doing this too often and (b) it's OK, you'll be fine 🌸
 */
export type Fabric3SidebarItem = {
  /**
   * The grouping of the app in the sidebar. This is simply meant to be an I18n
   * _key_ that _must_ be translated!
   */
  grouping: ApplicationGrouping;

  /**
   * The icon to be displayed in the sidebar. Note that the type really is
   * `GKIconName` but we're not checking this for now.
   */
  icon: string;

  /**
   * The label of the item that will be displayed in the sidebar
   */
  label: string;

  /**
   * Specify a route that is handled by the Container's Root Router. If an app
   * has multiple routes we can specify here its child routes You should not
   * add "/" or the app namespace like "my-app/help" to the route, use only the
   * child route name like "help" The child routes will be concatenated with
   * the namespace to create the final route in the to property.
   */
  route?: string;

  /**
   * This is a URI that is _not_ handled by the Container's Root Router . You
   * would use this to link to an external resource (e.g.
   * `https://example.com`)
   *
   * NOTE: You cannot specify _both_ a `route` and and `href`! If you do, the
   * `href` is used and `route` is ignored.
   *
   * TODO: Implement this with `APPS_UPPER` in the Container. This is currently
   *       only used by `APPS_LOWER`.
   */
  href?: string;

  /**
   * The order of the menu item in the sidebar.
   * */
  weight: number;

  /**
   * Tooltip to show when the app is disabled/grayed out. If your app is
   * disabled and you _don't_ specify this property in your `package.json`, the
   * Container will default it to "Coming soon!" (or a similar message).
   *
   * NOTE: When we Internationalize, this must be a _key_ and not a string!
   */
  disabledTooltip?: string;

  /**
   * We may want to show a small badge next to the app label. The text is
   * translated (or at least it should be) and the color is whatever's allowed
   * by `GKBadge` (e.g. `primary`) _or_ any valid CSS color (e.g. `#336699`)
   */
  badge?: {
    label: string;
    color: string;
  };

  /**
   * Permission model to use to gate this sidebar item. Check the "Frontend" namespace of Authz
   * models for valid values. The app will be hidden if an invalid model name is entered, and
   * allowed if no model name is entered.
   */
  permissionModel?: string;

  /**
   * Whether this app requires an active operating context (selected operation/agency)
   * to render. When false or absent, the app renders without the NoContextGuard.
   *
   * Default: false — apps that need context should explicitly set this to true.
   * Profile and support are examples of apps that work without a selected context.
   */
  requiresContext?: boolean;
};

/**
 * What we expect a library to have under its `fabric3` key, which is really
 * very little. Note that there is no `publish` key. All libraries are bundled
 * into the Container. (a) Fewer mistakes and (b) things are (or ought to be)
 * lazily loaded anyway.
 */
export type PackageDataLibrary = PackageJson & {
  fabric3: Fabric3Info;
};

export type PackageDataApp = PackageJson & {
  fabric3: Fabric3Info & {
    publish: boolean;
    sidebar: Fabric3SidebarItem[];
  };
};

/**
 * ═════════════════════════════════════════════════════════════════════════
 *                          RUNTIME TYPINGS
 * ═════════════════════════════════════════════════════════════════════════
 */

/**
 * An object conforming to this type is used by the Container to set app
 * visibility and routability, and map each app to its namespace/route.
 * Importantly, this is _built-up_ from each app's `package.json` and includes
 * all the sidebar info.
 */
export type FabricApp = Fabric3SidebarItem & {
  /**
   * Control app visiblity in the sidebar. If this is `false`, you may not
   * see it in the sidebar but it _may_ be _routable_. Think of a super-secret
   * admin app that you don't want people to know about (e.g. `/support` which
   * is actually in this monorepo!)
   *
   * NOTE: This is controlled via feature-flags and is _not_ set via
   * `package.json`, which is why it is specified here as a run-time property.
   *
   * NOTE: All apps are always visible when you run the Container locally!
   */
  visible: boolean;

  /**
   * Control whether or not the app is routable. If set to `false`, the
   * Container will show a helpful "Are you lost, little kitten?" message (or
   * something like it).
   *
   * Note that if an app is _visible_ but NOT _routable_, it is in a disabled
   * state in the sidebar.
   */
  routable: boolean;

  /**
   * Since we can have multiple sidebar items for the same app, the namespace
   * here is the name of the app itself.
   */
  namespace: string;

  /**
   * The React component that will be rendered when the user clicks on the
   * sidebar/Core Nav item.
   */
  component:
    | LazyExoticComponent<ComponentType<unknown>>
    | FunctionComponent
    | (() => React.ReactNode);

  /**
   * Specified in case a sidebar item links to something _external_ (i.e., not
   * controlled by the Container's Router).
   */
  href?: string;

  /**
   * This is the _final_ route of the app, using the namespace combined with
   * the route. E.g.
   *
   *    route: `help`, namespace: `my-app` 👉 to: `/my-app/help`
   *
   * If route is not specified, the "to" will be the same as the namespace,
   * which is a fancy word for "the app's folder name in `apps/`"
   * E.g.
   *
   *    namespace: `my-app` 👉 to: `/my-app`
   */
  to: string;

  /**
   * Permissions
   */
  permissions?: AppPermissions;
};

/**
 * GDS does not provide an enum or type for Theme Colors[1]. See the `Loading`
 * and `BigCenteredMessage` components in the Core for how this is used.
 *
 * [1]: https://design.granular.ag/v2/design-system/foundations/Colors
 *
 * TODO: Ask \@robglazebrook to provide a `themeColor` export like he does
 * with (for example) `patternColor`
 */
export type GDSThemeColor =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "light"
  | "dark";
